<script setup lang="ts" generic="T">
/**
 * A selection component that retrieves and displays items asynchronously.
 * @param {T} T - The type of the items to be selected.
 */

const props = defineProps({
  items: null,
  placeholder: null
})

const emit = defineEmits(["select", "cancel"])

const query = defineModel<string>("query", { default: "" })
const modelValue = defineModel<T | null>()
const selection = ref<number | null>(null)
const input = useTemplateRef("input")

const selectItem = (item: T) => {
  modelValue.value = item
  query.value = ""
  emit("select", item)
}

const selectCurrentItem = () => {
  if (props.items.length === 0 || !isDefined(selection.value))
    return
  selectItem(props.items[selection.value])
}

const selectItemAbove = () => {
  if (selection.value === null || selection.value === 0 || selection.value >= props.items.length)
    selection.value = props.items.length - 1
  else
    selection.value--
}

const selectItemBelow = () => {
  if (selection.value === null || selection.value >= props.items.length - 1)
    selection.value = 0
  else
    selection.value++
}

onMounted(async () => {
  await nextTick()
  input.value?.focus()
})
</script>

<template>
  <dropdown-menu class="menu-select-async">
    <menu-section class="menu-select-wrapper">
      <input
        ref="input"
        v-model="query"
        type="text"
        class="menu-select-input"
        :placeholder
        @keydown.enter.stop.prevent="selectCurrentItem()"
        @keydown.esc.stop.prevent="emit('cancel')"
        @keydown.up.prevent="selectItemAbove()"
        @keydown.down.prevent="selectItemBelow()"
      >
    </menu-section>

    <menu-separator />

    <menu-section class="menu-select-items">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="menu-select-option"
        @click="selectItem(item)"
      >
        <slot
          name="option"
          :item
          :selected="index === selection"
        />
      </div>
    </menu-section>
  </dropdown-menu>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.menu-select-async
  @apply relative w-full

.menu-select-wrapper
  @apply relative flex items-center justify-between

.menu-select-input
  @apply w-full
  @apply bg-transparent focus:outline-none
  &::placeholder
    @apply text-$global-t-tertiary

.menu-select-items
  @apply

.menu-select-option
  @apply cursor-pointer
</style>
